import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Server,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  LogOut,
  Shield,
  Trash2,
  Edit2,
  Key,
  Check,
  X,
  BarChart3,
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  name: string | null;
  tier: 'free' | 'starter' | 'professional';
  max_devices: number;
  device_count: number;
  email_verified: boolean;
  auth_provider: 'email' | 'google';
  created_at: string;
}

interface Stats {
  users: {
    total: number;
    by_tier: Record<string, number>;
    verified: number;
    unverified: number;
    new_this_week: number;
    new_this_month: number;
  };
  devices: {
    total: number;
    linked: number;
    unlinked: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1,
  });
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    tier: '',
    max_devices: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const getAdminHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
  });

  const fetchWithAuth = async <T,>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> => {
    const response = await fetch(`/api${endpoint}`, {
      ...options,
      headers: getAdminHeaders(),
    });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('adminToken');
      navigate('/admin');
      return { success: false, error: 'Unauthorized' };
    }

    return response.json();
  };

  const loadStats = useCallback(async () => {
    const response = await fetchWithAuth<Stats>('/admin/stats');
    if (response.success && response.data) {
      setStats(response.data);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    const params = new URLSearchParams({
      page: pagination.page.toString(),
      limit: pagination.limit.toString(),
    });
    if (search) params.append('search', search);
    if (tierFilter) params.append('tier', tierFilter);

    const response = await fetchWithAuth<{
      users: User[];
      pagination: typeof pagination;
    }>(`/admin/users?${params.toString()}`);

    if (response.success && response.data) {
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    }
    setIsLoading(false);
  }, [pagination.page, pagination.limit, search, tierFilter]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    loadStats();
    loadUsers();
  }, [loadStats, loadUsers, navigate]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadUsers();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      tier: user.tier,
      max_devices: user.max_devices,
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    setIsSubmitting(true);

    const response = await fetchWithAuth<User>(`/admin/users/${editingUser.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: editForm.name || undefined,
        tier: editForm.tier,
        max_devices: editForm.max_devices,
      }),
    });

    if (response.success) {
      setEditingUser(null);
      loadUsers();
      loadStats();
    }
    setIsSubmitting(false);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    const response = await fetchWithAuth(`/admin/users/${userId}`, {
      method: 'DELETE',
    });

    if (response.success) {
      loadUsers();
      loadStats();
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUserId || !newPassword) return;
    setIsSubmitting(true);

    const response = await fetchWithAuth(`/admin/users/${resetPasswordUserId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    });

    if (response.success) {
      setResetPasswordUserId(null);
      setNewPassword('');
    }
    setIsSubmitting(false);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const tierColors = {
    free: 'bg-zinc-500/20 text-zinc-400',
    starter: 'bg-blue-500/20 text-blue-400',
    professional: 'bg-violet-500/20 text-violet-400',
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/25">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Dashboard</h1>
              <p className="text-xs text-zinc-500">WatchTheSize Management</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <Users className="w-5 h-5 text-violet-400" />
                </div>
                <span className="text-sm text-zinc-400">Total Users</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.users.total}</p>
              <p className="text-xs text-emerald-400 mt-1">
                +{stats.users.new_this_week} this week
              </p>
            </div>

            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Server className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-sm text-zinc-400">Total Devices</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.devices.total}</p>
              <p className="text-xs text-zinc-500 mt-1">{stats.devices.linked} linked</p>
            </div>

            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Check className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-sm text-zinc-400">Verified</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.users.verified}</p>
              <p className="text-xs text-zinc-500 mt-1">{stats.users.unverified} pending</p>
            </div>

            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <BarChart3 className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-sm text-zinc-400">By Tier</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-zinc-400">F: {stats.users.by_tier.free || 0}</span>
                <span className="text-blue-400">S: {stats.users.by_tier.starter || 0}</span>
                <span className="text-violet-400">P: {stats.users.by_tier.professional || 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Users Section */}
        <div className="bg-white/[0.02] rounded-xl border border-white/5 overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-white/5 flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email or name..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 text-sm"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2.5 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-600 transition-colors"
              >
                Search
              </button>
            </form>

            <select
              value={tierFilter}
              onChange={(e) => {
                setTierFilter(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
            </select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No users found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider py-3 px-4">
                      User
                    </th>
                    <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider py-3 px-4">
                      Tier
                    </th>
                    <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider py-3 px-4">
                      Devices
                    </th>
                    <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider py-3 px-4">
                      Status
                    </th>
                    <th className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider py-3 px-4">
                      Joined
                    </th>
                    <th className="text-right text-xs font-medium text-zinc-500 uppercase tracking-wider py-3 px-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-sm font-medium text-white">{user.name || 'Unnamed'}</p>
                          <p className="text-xs text-zinc-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex px-2 py-1 rounded-md text-xs font-medium ${tierColors[user.tier]}`}
                        >
                          {user.tier}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-zinc-300">
                        {user.device_count} / {user.max_devices}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          {user.email_verified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-emerald-500/20 text-emerald-400">
                              <Check className="w-3 h-3" />
                              Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-500/20 text-amber-400">
                              Pending
                            </span>
                          )}
                          <span className="text-xs text-zinc-500">{user.auth_provider}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-zinc-400">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/10 transition-colors"
                            title="Edit user"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {user.auth_provider === 'email' && (
                            <button
                              onClick={() => setResetPasswordUserId(user.id)}
                              className="p-2 rounded-lg text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                              title="Reset password"
                            >
                              <Key className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="p-4 border-t border-white/5 flex items-center justify-between">
              <p className="text-sm text-zinc-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} users
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-zinc-400">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.total_pages}
                  className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Edit User Modal */}
      {editingUser && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => setEditingUser(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-zinc-900 rounded-xl border border-white/10 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">Edit User</h2>
                <button
                  onClick={() => setEditingUser(null)}
                  className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">Email</label>
                  <input
                    type="text"
                    value={editingUser.email}
                    disabled
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-zinc-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">Tier</label>
                  <select
                    value={editForm.tier}
                    onChange={(e) => {
                      const tier = e.target.value;
                      const maxDevices = tier === 'free' ? 1 : tier === 'starter' ? 10 : 20;
                      setEditForm((prev) => ({ ...prev, tier, max_devices: maxDevices }));
                    }}
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  >
                    <option value="free">Free</option>
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">Max Devices</label>
                  <input
                    type="number"
                    value={editForm.max_devices}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, max_devices: parseInt(e.target.value) || 1 }))
                    }
                    min={1}
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg bg-violet-500 text-white font-medium hover:bg-violet-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUserId && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => setResetPasswordUserId(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-zinc-900 rounded-xl border border-white/10 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">Reset Password</h2>
                <button
                  onClick={() => setResetPasswordUserId(null)}
                  className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">New Password</label>
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 placeholder-zinc-500"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Min 8 characters</p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setResetPasswordUserId(null)}
                  className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={isSubmitting || newPassword.length < 8}
                  className="px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

